import React, { Children, createRef, useRef } from 'react';
import styled, { css } from 'styled-components';
import { useEffect, useCallback } from 'react';
import { useState } from 'react';
import Theme from '@styles/Theme';

const fixedStyle = css<{
  grid?: number;
}>`
  display: grid;
  grid-template-columns: ${({ grid }) => grid && `repeat(${grid}, 1fr)`};
`;

const Wrapper = styled.div<{
  grid?: number;
}>`
  height: 40px;
  border-radius: 8px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  position: relative;

  ${({ grid }) => grid && fixedStyle};
`;

const activeStyle = css`
  color: var(--textColor);
  font-weight: 700;

  svg {
    path {
      fill: var(--textColor);
    }
  }
`;

const Item = styled.button<{
  isActive: boolean;
}>`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  transition-delay: color 0.3s;
  transition: color 0.2s, background-color 0.2s, border 0.2s;
  padding: 0 12px;
  font-size: 13px;
  color: var(--subTextColor);
  z-index: 1;
  border: none;
  background: transparent;
  cursor: var(--hoverCursorURL), pointer;

  span, div {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
  }

  svg {
    path {
      fill: var(--subTextColor);
    }
  }

  &:hover {
    background-color: ${({ isActive }) => !isActive && `rgba(0,0,0,0.1)`};
  }
  ${({ isActive }) => isActive && activeStyle};
`;

const ActiveBar = styled.div`
  width: 300px;
  height: 32px;
  box-shadow: ${Theme.shadow.card};
  border: 1px solid var(--lineColor);
  background-color: var(--cardBackgroundColor);

  border-radius: 8px;
  z-index: 0;
  position: absolute;
  transition: left 0.2s ease, width 0.2s ease;
`;

// Key 값에서 React.Children.toArray()로 인해 추가된 ".$" 를 제거하는 함수
const sanitizeKey = (key: string | null | undefined) =>
  key?.replace(/^.\$/, '') || '';

type Props = {
  children: React.ReactNode[];
  gridFix?: number;
  style?: React.CSSProperties;
  setKey?: any;
  customClick?: (key) => void;
  defaultKey?: string;
};

interface IchildProps {
  key: string;
  props: {
    children: React.ReactNode[] | React.ReactNode | string;
  };
}

function ToggleTab({
  children,
  gridFix,
  style,
  setKey,
  customClick,
  defaultKey,
}: Props) {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const activeBarRef = useRef<HTMLDivElement>(null);

  const tabRefs: any = useRef(
    Array.from({ length: children.length }, () => createRef()),
  );
  const [activeBarWidth, setActiveBarWidth] = useState<number>(30);
  const [moveToLeftValue, setMoveToLeftValue] = useState<number>(0);
  const buttonMargin = 4;

  useEffect(() => {
    if (defaultKey && children.length > 0) {
      const findIndex = children.findIndex(
        (child: any) => sanitizeKey(child.key) === defaultKey,
      );
      setCurrentIndex(findIndex);
    }
  }, [defaultKey, children]);

  const calcBtnDistance = useCallback(
    (btnWidthArr: number[], index: number): number => {
      if (index <= 0) return 0;
      return btnWidthArr.slice(0, index).reduce((acc, curr) => acc + curr, 0);
    },
    [],
  );

  /*
   *
   *
   *
   *
   * tab button을 따라다니는 bar */
  const activeBarStyle = useCallback(() => {
    if (tabRefs.current) {
      const tabBtnWidthArr = tabRefs.current.map((ref) =>
        ref?.current ? ref.current.clientWidth : 0,
      );

      const btnLeft = calcBtnDistance(tabBtnWidthArr, currentIndex);
      const moveToLeft = btnLeft + buttonMargin;
      setMoveToLeftValue(moveToLeft);
    }
  }, [currentIndex, buttonMargin, calcBtnDistance]);

  const calcBtnWidth = useCallback(() => {
    if (
      tabRefs.current &&
      tabRefs.current[currentIndex] &&
      tabRefs.current[currentIndex].current
    ) {
      const tabBtnWidth = tabRefs.current[currentIndex].current.clientWidth;
      setActiveBarWidth(() => tabBtnWidth);
    }
  }, [currentIndex]);

  const handleClick = useCallback(
    (index: number, childKey: string | undefined) => {
      setCurrentIndex(index);
      if (childKey && setKey) setKey(childKey);
      if (customClick) customClick(childKey);
    },
    [],
  );

  useEffect(() => {
    activeBarStyle();
    calcBtnWidth();
  }, [currentIndex]);

  return (
    <Wrapper
      style={{ padding: buttonMargin, ...style }}
      grid={gridFix && children.length}
    >
      <ActiveBar
        ref={activeBarRef}
        style={{
          width: activeBarWidth,
          left: moveToLeftValue,
          top: buttonMargin,
        }}
      />
      {Children.map(children, (child: IchildProps, index) => {
        if (!child) return null;
        // child.key  === children의 props 중, key값 받아옴
        return (
          <Item
            ref={tabRefs.current[index]}
            onClick={() => {
              handleClick(index, child.key);
            }}
            isActive={currentIndex === index}
          >
            {child}
          </Item>
        );
      })}
    </Wrapper>
  );
}

export default React.memo(ToggleTab);
